import { useNavigate, useSearchParams } from "react-router-dom";

import {
  useResetPasswordMutation,
  useVerifyTokenQuery
} from "@/api/auth/useAuthAPI";
import LoginPageLayout from "@/components/layout/login";
import { PASSWORD_FORM_TOAST_CONFIG } from "@/components/passwordForm/data";
import ExpiredLink from "@/components/passwordForm/ExpiredLink";
import LoadingVerifyToken from "@/components/passwordForm/LoadingVerifyToken";
import PasswordForm from "@/components/passwordForm/PasswordForm";
import useToast from "@/hooks/ui/useToast";

export const RESET_PASSWORD_TOAST_COPY = {
  success: {
    title: "Password Updated Successfully",
    message:
      "Your password has been changed. You can now sign in with new credentials"
  },
  error: {
    title: "Something Went Wrong",
    message: "Sorry, we couldn't process your request. Please try again."
  }
};

const ResetPasswordPage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { isError: verifyTokenError, isPending: verifyTokenPending } =
    useVerifyTokenQuery(searchParams.get("token") ?? "");

  const { mutate: resetPassword, isPending } = useResetPasswordMutation({
    onSuccess: () => {
      navigate("/login", { replace: true });
      showToast(
        PASSWORD_FORM_TOAST_CONFIG(
          RESET_PASSWORD_TOAST_COPY.success.title,
          RESET_PASSWORD_TOAST_COPY.success.message
        ).successToast
      );
    },
    onError: () => {
      showToast(
        PASSWORD_FORM_TOAST_CONFIG(
          RESET_PASSWORD_TOAST_COPY.error.title,
          RESET_PASSWORD_TOAST_COPY.error.message
        ).errorToast
      );
    }
  });

  if (verifyTokenPending) return <LoadingVerifyToken />;
  if (verifyTokenError) return <ExpiredLink />;

  return (
    <LoginPageLayout>
      <div className="flex flex-col flex-1 mt-[278px] lg:mt-[278px] xl:mt-[209px] 2xl:mt-[394px] w-full mx-auto">
        <PasswordForm
          mode="resetPassword"
          isSubmitting={isPending}
          onSubmit={(password) => {
            resetPassword({
              token: searchParams.get("token") as string,
              password
            });
          }}
        />
      </div>
    </LoginPageLayout>
  );
};

export default ResetPasswordPage;
